import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, Sparkles, RotateCcw } from 'lucide-react';
import { apiFetch } from '../utils/api';


export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => 'chat_' + Math.random().toString(36).slice(2, 10));
  const [welcome, setWelcome] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await apiFetch(`/api/chat-support/settings`);
        if (res.ok) {
          const cfg = await res.json();
          setEnabled(cfg.enabled === true);
          setWelcome(cfg.welcome_message || 'Γεια σας! Ρωτήστε με οτιδήποτε για το Parkingevo.');
        }
      } catch {}
    };
    fetchSettings();
    // Retry after 3s in case initial fetch happens before login completes
    const timer = setTimeout(fetchSettings, 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setSending(true);
    try {
      const res = await apiFetch(`/api/chat-support/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, session_id: sessionId }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'bot', text: data.answer, category: data.category }]);
      } else {
        setMessages(prev => [...prev, { role: 'bot', text: 'Σφάλμα. Δοκιμάστε ξανά.' }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'bot', text: 'Σφάλμα σύνδεσης.' }]);
    }
    setSending(false);
  };

  const clearChat = () => {
    setMessages([{ role: 'bot', text: welcome }]);
    apiFetch(`/api/chat-support/clear-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    }).catch(() => {});
  };

  if (!enabled) return null;

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => {
            setOpen(true);
            if (messages.length === 0 && welcome) {
              setMessages([{ role: 'bot', text: welcome }]);
            }
          }}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#004C98] text-white shadow-xl hover:bg-[#003A75] transition-all flex items-center justify-center hover:scale-110 group"
          data-testid="chat-widget-button"
        >
          <MessageCircle size={26} />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
            <Sparkles size={10} className="text-amber-900" />
          </span>
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          data-testid="chat-widget-window">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#004C98] to-[#0066CC] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles size={18} />
              </div>
              <div>
                <p className="font-semibold text-sm">AI Βοηθός</p>
                <p className="text-[11px] text-blue-200">Parkingevo Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={clearChat} className="hover:bg-white/20 rounded-full p-1.5 transition-colors" title="Νέα συνομιλία" data-testid="chat-clear-btn">
                <RotateCcw size={15} />
              </button>
              <button onClick={() => setOpen(false)} className="hover:bg-white/20 rounded-full p-1.5 transition-colors" data-testid="chat-close-btn">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#004C98] text-white rounded-br-sm'
                    : 'bg-white text-slate-700 border border-slate-200 rounded-bl-sm shadow-sm'
                }`}>
                  {msg.role === 'bot' && msg.category === 'AI' && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-500 mb-1">
                      <Sparkles size={10} /> AI
                    </span>
                  )}
                  <div className="whitespace-pre-wrap">{msg.text}</div>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin text-blue-500" />
                  <span className="text-xs text-slate-400">Σκέφτομαι...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick suggestions */}
          {messages.length <= 1 && (
            <div className="px-4 py-2 bg-white border-t border-slate-100 flex gap-1.5 overflow-x-auto flex-shrink-0">
              {['Πώς κάνω είσοδο;', 'Τιμές στάθμευσης;', 'Μηνιαία ενοικίαση'].map((q, i) => (
                <button key={i} onClick={() => { setInput(q); }}
                  className="text-[11px] px-2.5 py-1.5 bg-blue-50 text-blue-700 rounded-full whitespace-nowrap hover:bg-blue-100 transition-colors border border-blue-100"
                  data-testid={`quick-suggestion-${i}`}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t border-slate-200 p-3 flex gap-2 bg-white flex-shrink-0">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="Ρωτήστε οτιδήποτε..."
              className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#004C98]/30 focus:border-[#004C98]"
              data-testid="chat-input"
            />
            <button
              onClick={handleSend}
              disabled={sending || !input.trim()}
              className="bg-[#004C98] text-white rounded-xl px-3.5 py-2.5 hover:bg-[#003A75] disabled:opacity-40 transition-colors"
              data-testid="chat-send-btn"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
